import { useState, useRef, useEffect, useCallback } from "react";
import { Tabs, Button, Input, Modal, Typography, Tooltip, message, Pagination } from "antd";
import { SearchOutlined, EditOutlined, DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { connect } from "react-redux";
import { getUserInfo } from "redux/selectors";
import styled from "styled-components";
import COLORS from "common/Colors";

const { Title, Text } = Typography;

const PAGE_LIMIT = 10;

// ── Styled Buttons (matching Task.jsx style) ──────────────────────────────────

const CancelBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 24px;
    border-radius: 8px;
    border: 1px solid #a6a7aa;
    color: #374151;
    font-weight: 500;
    background: #fff;
  }

  &&:hover {
    border-color: #9ca3af;
    color: #111827;
    background: #f9fafb;
  }
`;

const ConfirmBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 28px;
    border-radius: 8px;
    background-color: #00685F !important;
    border: none;
    color: #ffffff !important;
    font-weight: 600;
  }

  &&:hover,
  &&:focus {
    background-color: #00685F !important;
    color: #ffffff !important;
    border: none;
  }
`;
const CreateBtn = styled(Button)`
  && {
    height: 38px;
    padding: 0 20px;
    border-radius: 6px;
    background: ${COLORS.PRIMARY}; 
    border: none;
    color: #ffffff !important;
    font-weight: 600;
    box-shadow: none;
  }

  &&:hover,
  &&:focus {
    background: ${COLORS.PRIMARY};
    border: none;
    color: #ffffff !important;
  }
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-nav::before {
    border-bottom: 1px solid #e5e7eb;
  }

  .ant-tabs-tab {
    padding: 8px 12px;
    margin: 0 16px 0 0;
    color: #9ca3af;
    font-weight: 400;
  }

  .ant-tabs-tab:hover {
    color: #374151;
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #00685F !important;
    font-weight: 600;
  }

  .ant-tabs-ink-bar {
    background: #00685F !important;
    height: 2px;
    border-radius: 2px;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content { border-radius: 12px; }
  
`;

// ─── Create / Edit Modal ──────────────────────────────────────────────────────
function CreateEditModal({ open, onClose, onSubmit, mode, initialValue, entityLabel }) {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    if (open) setValue(initialValue || "");
  }, [open, initialValue]);

  const isEdit = mode === "edit";

  const handleSubmit = async () => {
    if (!value.trim()) return;
    try {
      await onSubmit(value.trim(), onClose);
    } catch (e) {
      message.error("Action failed");
    }
  };

  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={420}
      closable={false}
      centered
      destroyOnClose
    >
      <div style={{
        textAlign: "left",
        padding: "24px 16px 8px",
      }}>
        {/* Title */}
        <p style={{
          fontSize: 15,
          fontWeight: 500,
          color: "#111827",
          marginBottom: 16,
        }}>
          {isEdit ? `Edit ${entityLabel}` : `Create ${entityLabel}`}
        </p>

        {/* Input */}
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`Enter ${entityLabel.toLowerCase()} name`}
          size="large"
          style={{ borderRadius: 8 }}
        />

        {/* Buttons */}
        <div style={{
          display: "flex",
          justifyContent: "flex-end",  // ← SAME as delete modal
          gap: 16,
          marginTop: 32,

        }}>
          <CancelBtn style={{ width: 120 }} onClick={onClose}>
            Cancel
          </CancelBtn>

          <ConfirmBtn className="ant-btn-primary" style={{ width: 120 }} onClick={handleSubmit}>
            {isEdit ? "Update" : "Create"}
          </ConfirmBtn>
        </div>
      </div>
    </StyledModal>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteConfirmModal({ open, onClose, onConfirm, name }) {
  return (
    <StyledModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={420}
      closable={false}
      centered
      destroyOnClose
    >
      <div style={{ textAlign: "center", padding: "24px 16px 8px" }}>
        <p style={{ fontSize: 15, fontWeight: 500, color: "#111827", marginBottom: 32 }}>
          Are you sure you want to delete <strong>"{name}"</strong>?
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
          <CancelBtn onClick={onClose} style={{ width: 100 }}>No</CancelBtn>
          <ConfirmBtn onClick={() => { onConfirm(); onClose(); }} style={{ width: 100 }}>Yes</ConfirmBtn>
        </div>
      </div>
    </StyledModal>
  );
}

// ─── Icon button ──────────────────────────────────────────────────────────────
const ActionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  background: #DEEFEE;
  color: #00685F;
  transition: opacity 0.15s;
`;

function IconBtn({ icon, tooltip, onClick }) {
  return (
    <Tooltip title={tooltip}>
      <ActionIcon
        onClick={e => { e.stopPropagation(); onClick && onClick(); }}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
}

// ─── Simple Table (Task Type — no drag handle) ────────────────────────────────
function SimpleTable({ rows, onEdit, onDelete, entityLabel }) {
  const [hoveredId, setHoveredId] = useState(null);

  const colWidths = ["1fr", "1fr", "1fr", "1fr", "120px"];

  const headerStyle = {
    display: "grid",
    gridTemplateColumns: colWidths.join(" "),
    padding: "12px 20px",
    background: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
    fontSize: 14,
    color: "#888",
    fontWeight: 600,
    borderRadius: "10px 10px 0 0",
  };

  return (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
      <div style={headerStyle}>
        <span>Type</span>
        <span>Recent Update</span>
        <span>Updated on</span>
        <span>Active Tasks</span>
        <span />
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px 20px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
          No data found.
        </div>
      ) : (
        rows.map((row, i) => (
          <div
            key={row.id}
            onMouseEnter={() => setHoveredId(row.id)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              display: "grid",
              gridTemplateColumns: colWidths.join(" "),
              alignItems: "center",
              padding: "14px 20px",
              borderBottom: i < rows.length - 1 ? "1px solid #f5f5f5" : "none",
              background: hoveredId === row.id ? "#f6fffe" : "#fff",
              transition: "background 0.12s",
              fontSize: 14,
              color: "#333",
            }}
          >
            <Text style={{ fontSize: 14 }}>{row.name}</Text>
            <Text style={{ color: "#555", fontSize: 14 }}>{row.recentUpdate}</Text>
            <Text style={{ color: "#555", fontSize: 14 }}>{row.updatedOn}</Text>
            <Text style={{ fontSize: 14 }}>{row.activeTasks}</Text>
            <div style={{
              display: "flex", gap: 6, justifyContent: "flex-end",
              opacity: hoveredId === row.id ? 1 : 0,
              transition: "opacity 0.15s",
            }}>
              {!row.isDefault && <IconBtn icon={<EditOutlined />} tooltip={`Edit ${entityLabel}`} onClick={() => onEdit(row)} />}
              {!row.isDefault && (
                <IconBtn icon={<DeleteOutlined />} tooltip={`Delete ${entityLabel}`} onClick={() => row.activeTasks ? message.warning(`Cannot delete "${row.name}": it has ${row.activeTasks} active task${row.activeTasks > 1 ? "s" : ""} assigned to it.`) : onDelete(row)} />
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Draggable Table (Task Status — with drag handle) ─────────────────────────
function DraggableTable({ rows, onReorder, onEdit, onDelete, entityLabel }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e, i) => { e.preventDefault(); setDragOverIndex(i); };
  const handleDrop = (i) => {
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setDragOverIndex(null); return; }
    const reordered = [...rows];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(i, 0, moved);
    onReorder(reordered);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const colWidths = ["48px", "1fr", "1fr", "1fr", "1fr", "120px"];

  return (
    <div style={{ border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" }}>
      <div style={{
        display: "grid", gridTemplateColumns: colWidths.join(" "),
        padding: "12px 16px", background: "#fafafa",
        borderBottom: "1px solid #f0f0f0", fontSize: 14, color: "#888",
        fontWeight: 600, borderRadius: "10px 10px 0 0",
      }}>
        <span />
        <span>Type</span>
        <span>Recent Update</span>
        <span>Updated on</span>
        <span>Active Tasks</span>
        <span />
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px 20px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
          No data found.
        </div>
      ) : (
        rows.map((row, i) => (
          <div
            key={row.id}
            draggable
            onDragStart={() => handleDragStart(i)}
            onDragOver={e => handleDragOver(e, i)}
            onDrop={() => handleDrop(i)}
            onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
            onMouseEnter={() => setHoveredId(row.id)}
            onMouseLeave={() => setHoveredId(null)}
            style={{
              display: "grid", gridTemplateColumns: colWidths.join(" "),
              alignItems: "center", padding: "14px 16px",
              borderBottom: i < rows.length - 1 ? "1px solid #f5f5f5" : "none",
              background: dragOverIndex === i ? "#f0fdf4" : hoveredId === row.id ? "#f6fffe" : "#fff",
              transition: "background 0.12s", fontSize: 14, color: "#333", cursor: "default",
              borderLeft: dragOverIndex === i ? "2px solid #10b981" : "2px solid transparent",
            }}
          >
            <span style={{ color: "#ccc", cursor: "grab", fontSize: 16, display: "flex", alignItems: "center" }}>
              <HolderOutlined />
            </span>
            <Text style={{ fontSize: 14 }}>{row.name}</Text>
            <Text style={{ color: "#555", fontSize: 14 }}>{row.recentUpdate}</Text>
            <Text style={{ color: "#555", fontSize: 14 }}>{row.updatedOn}</Text>
            <Text style={{ fontSize: 14 }}>{row.activeTasks}</Text>
            <div style={{
              display: "flex", gap: 6, justifyContent: "flex-end",
              opacity: hoveredId === row.id ? 1 : 0, transition: "opacity 0.15s",
            }}>
              {!row.isDefault && <IconBtn icon={<EditOutlined />} tooltip={`Edit ${entityLabel}`} onClick={() => onEdit(row)} />}
              {!row.isDefault && (
                <IconBtn icon={<DeleteOutlined />} tooltip={`Delete ${entityLabel}`} onClick={() => row.activeTasks ? message.warning(`Cannot delete "${row.name}": it has ${row.activeTasks} active task${row.activeTasks > 1 ? "s" : ""} assigned to it.`) : onDelete(row)} />
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────
function TabContent({
  entityLabel, createLabel, draggable = false,
  fetchList, onCreate, onEdit, onDelete, onReorder, userid,
}) {
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  useEffect(() => {
    if (!search.trim()) {
      setRows(allRows);
      setTotalCount(allRows.length);
    } else {
      const filtered = allRows.filter(r =>
        r.name?.toLowerCase().includes(search.toLowerCase())
      );
      setRows(filtered);
      setTotalCount(filtered.length);
    }
  }, [search, allRows]);

  const loadData = async (page = 1, searchVal = "") => {
    if (!userid) return;
    setLoading(true);
    try {
      const result = await fetchList(page, PAGE_LIMIT, "");
      setAllRows(result?.data || []);
      setRows(result?.data || []);
      setTotalCount(result?.total ?? result?.data?.length ?? 0);
      setCurrentPage(page);
    } catch (e) {
      console.error(`[TabContent:${entityLabel}] loadData error -->`, e);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData(1, "");
  }, [userid]);

  const handleCreate = async (name, onClose) => {
    const res = await onCreate(name);
    if (res?.code === "ALREADY_EXISTS" || res?.message?.toLowerCase().includes("already exists")) {
      message.error(`This ${entityLabel} name already exists.`);
      return;
    }
    await loadData(currentPage);
    message.success(`${entityLabel} created successfully.`);
    onClose();
  };

  const handleEdit = async (name, onClose) => {
    const res = await onEdit(editRow, name);
    if (res?.code === "ALREADY_EXISTS" || res?.message?.toLowerCase().includes("already exists")) {
      message.error(`This ${entityLabel} name already exists.`);
      return;
    }
    await loadData(currentPage);
    message.success(`${entityLabel} updated successfully.`);
    onClose();
  };

  const handleDelete = async () => {
    await onDelete(deleteRow);
    const newPage = rows.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
    await loadData(newPage);
    message.success(`${entityLabel} deleted successfully.`);
  };

  const handleReorder = async (reordered) => {
    setRows(reordered);
    if (onReorder) await onReorder(reordered);
  };

  const handlePageChange = (page) => {
    loadData(page, search);
  };

  return (
    <div>
      {/* Filters row */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "1.5rem",
        marginBottom: "1rem",
        flexWrap: "wrap",
        gap: 10,
      }}>
        <span style={{ fontWeight: 600, fontSize: 16, color: "#111827" }}>
          Total {entityLabel}: {totalCount}
        </span>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ minWidth: 180, maxWidth: 220, borderRadius: 20, height: 38 }}
          />
          <CreateBtn onClick={() => setCreateOpen(true)}>
            {createLabel}
          </CreateBtn>
        </div>
      </div>

      {/* Table */}
      {draggable ? (
        <DraggableTable
          rows={rows}
          onReorder={handleReorder}
          onEdit={(row) => setEditRow(row)}
          onDelete={(row) => setDeleteRow(row)}
          entityLabel={entityLabel}
        />
      ) : (
        <SimpleTable
          rows={rows}
          onEdit={(row) => setEditRow(row)}
          onDelete={(row) => setDeleteRow(row)}
          entityLabel={entityLabel}
        />
      )}

      {/* Pagination */}
      {rows.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Pagination
            current={currentPage}
            pageSize={PAGE_LIMIT}
            total={totalCount}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} ${entityLabel.toLowerCase()}s`}
            size="small"
          />
        </div>
      )}

      {/* Modals */}
      <CreateEditModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        mode="create"
        initialValue=""
        entityLabel={entityLabel}
      />
      <CreateEditModal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        onSubmit={handleEdit}
        mode="edit"
        initialValue={editRow?.name}
        entityLabel={entityLabel}
      />
      <DeleteConfirmModal
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDelete}
        name={deleteRow?.name}
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function TaskTypeStatus({ onBack, userInfo }) {
  const [activeTab, setActiveTab] = useState("type");

  // ── Task Type API calls ──────────────────────────────────────────────────
  const fetchTypes = async (page, limit, search) => {
    console.log("[fetchTypes] called with search:", search);
    const params = new URLSearchParams({
      userid: userInfo?.userid,
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append("search", search);
    console.log("[fetchTypes] URL params:", params.toString());

    const res = await CALL_FEATCH_API(
      `insyts-task/gettasktype?${params.toString()}`, "GET"
    );
    console.log("[fetchTypes] raw API response:", JSON.stringify(res?.response));
    const data = (res?.response?.data || []).map(t => ({
      id: t.typeid,
      name: t.typename,
      recentUpdate: t.updatedbyname || t.createdbyname || "—",
      updatedOn: t.updatedat || t.createdat
        ? new Date(t.updatedat || t.createdat).toLocaleString("en-GB", {
          day: "numeric", month: "short", year: "numeric",
          hour: "2-digit", minute: "2-digit", hour12: true,
        })
        : "—",
      activeTasks: parseInt(t.activetaskcount ?? 0, 10),
    }));
    return {
      data,
      total: res?.response?.total ?? res?.response?.pagination?.total ?? data.length,
    };
  };

  const createType = async (name) => {
    const res = await CALL_API("insyts-task/addtasktype", "post", {
      typename: name,
      userid: userInfo?.userid,
      userName: userInfo?.name || null,
      createdby: userInfo?.userid || userInfo?.email,
    });
    return res;
  };

  const editType = async (row, name) => {
    const res = await CALL_API("insyts-task/edittasktype", "post", {
      typeid: row.id,
      typename: name,
      userid: userInfo?.userid,
      updatedby: userInfo?.userid || userInfo?.email,
      userName: userInfo?.name || null,
    });
    return res;
  };

  const deleteType = async (row) => {
    await CALL_API("insyts-task/deletetasktype", "post", {
      typeid: row.id,
      userid: userInfo?.userid,
    });
  };

  // ── Task Status API calls ────────────────────────────────────────────────
  const fetchStatuses = async (page, limit, search) => {
    console.log("[fetchStatuses] called with search:", search);
    const params = new URLSearchParams({
      userid: userInfo?.userid,
      page: String(page),
      limit: String(limit),
    });
    if (search) params.append("search", search);

    const res = await CALL_FEATCH_API(
      `insyts-task/gettaskstatus?${params.toString()}`, "GET"
    );

    // Hardcoded default statuses — these are always shown, never editable/deletable.
    const HARDCODED_STATUSES = [
      { key: "open", name: "Open" },
      { key: "inprogress", name: "In Progress" },
      { key: "completed", name: "Completed" },
    ];
    const normalize = (val) => (val || "").toLowerCase().replace(/\s+/g, "");
    const isHardcoded = (name) => HARDCODED_STATUSES.some(d => d.key === normalize(name));

    const apiRows = (res?.response?.data || []).map(s => {
      const isDefault = isHardcoded(s.statusname);
      return {
        id: s.statusid,
        name: s.statusname,
        sortorder: s.sortorder,
        isDefault,
        recentUpdate: isDefault ? "—" : (s.updatedbyname || s.createdbyname || "—"),
        updatedOn: isDefault ? "—" : (
          s.updatedat || s.createdat
            ? new Date(s.updatedat || s.createdat).toLocaleString("en-GB", {
              day: "numeric", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit", hour12: true,
            })
            : "—"
        ),
        activeTasks: parseInt(s.activetaskcount ?? 0, 10),
      };
    });

    // Make sure all 3 hardcoded statuses exist in the list, even if the API
    // response is missing one (e.g. it hasn't been created in the DB yet).
    const presentKeys = apiRows.filter(r => r.isDefault).map(r => normalize(r.name));
    const missingDefaults = HARDCODED_STATUSES
      .filter(d => !presentKeys.includes(d.key))
      .map(d => ({
        id: `default-${d.key}`,
        name: d.name,
        sortorder: -1,
        isDefault: true,
        recentUpdate: "—",
        updatedOn: "—",
        activeTasks: 0,
      }));

    const data = [...missingDefaults, ...apiRows];

    return {
      data,
      total: res?.response?.total ?? res?.response?.pagination?.total ?? data.length,
    };
  };

  const createStatus = async (name) => {
    const res = await CALL_API("insyts-task/addtaskstatus", "post", {
      statusname: name,
      userid: userInfo?.userid,
      userName: userInfo?.name || null,
      createdby: userInfo?.userid || userInfo?.email,
    });
    return res;
  };

  const editStatus = async (row, name) => {
    const res = await CALL_API("insyts-task/edittaskstatus", "post", {
      statusid: row.id,
      statusname: name,
      userid: userInfo?.userid,
      updatedby: userInfo?.userid || userInfo?.email,
      userName: userInfo?.name || null,
    });
    return res;
  };

  const deleteStatus = async (row) => {
    await CALL_API("insyts-task/deletetaskstatus", "post", {
      statusid: row.id,
      userid: userInfo?.userid,
    });
  };

  const reorderStatuses = async (reordered) => {
    await CALL_API("insyts-task/reordertaskstatus", "post", {
      userid: userInfo?.userid,
      statuses: reordered.map((s, i) => ({ statusid: s.id, sortorder: i })),
    });
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 24, fontSize: 14 }}>
        <span onClick={onBack} style={{ cursor: "pointer", color: "#555", fontWeight: 500 }}>
          Task
        </span>
        <span style={{ color: "#bbb", fontSize: 12 }}>›</span>
        <span style={{ color: "#111", fontWeight: 600 }}>Type &amp; Status</span>
      </div>

      {/* Tabs */}
      <StyledTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 0 }}
        items={[
          {
            key: "type",
            label: "Task Type",
            children: (
              <TabContent
                entityLabel="Type"
                createLabel="Create Type"
                draggable={false}
                fetchList={fetchTypes}
                onCreate={createType}
                onEdit={editType}
                onDelete={deleteType}
                userid={userInfo?.userid}
              />
            ),
          },
          {
            key: "status",
            label: "Task Status",
            children: (
              <TabContent
                entityLabel="Status"
                createLabel="Create Status"
                draggable={true}
                fetchList={fetchStatuses}
                onCreate={createStatus}
                onEdit={editStatus}
                onDelete={deleteStatus}
                onReorder={reorderStatuses}
                userid={userInfo?.userid}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(TaskTypeStatus);